// Tabs/OverviewTab.tsx
import { useState } from 'react';
import {
  Stack, Group, Text, SimpleGrid, Card, Button, Skeleton, Badge, Divider,
} from '@mantine/core';
import {
  IconMail, IconPhone, IconBriefcase2, IconUserMinus, IconMapPin,
  IconCircleDot, IconBook,
} from '@tabler/icons-react';
import { useFacilitatorService, FacilitatorModelView } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import ConfirmActionModal from './ConfirmActionModal';

interface OverviewTabProps {
  facilitator: FacilitatorModelView | null;
  active: boolean;
  courseCount: number;
  enrolmentCount: number;
  locationCount: number;
  onRemoved?: () => void;
  onTabChange?: (tab: string) => void;
}

function DetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <div style={{ color: 'var(--mantine-color-dimmed)', marginTop: 2 }}>{icon}</div>
      <Stack gap={2}>
        <Text size="xs" c="dimmed">{label}</Text>
        <Text size="sm" fw={600}>{value}</Text>
      </Stack>
    </Group>
  );
}

function StatItem({
  icon,
  count,
  label,
  color,
  onClick,
}: {
  icon: React.ReactNode;
  count: number;
  label: string;
  color: string;
  onClick?: () => void;
}) {
  return (
    <Stack
      gap={4}
      align="center"
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : undefined }}
    >
      <Group gap={6} align="center">
        <div style={{ color: `var(--mantine-color-${color}-4)` }}>{icon}</div>
        <Text fw={700} c={color}>{count}</Text>
      </Group>
      <Text size="sm" c="dimmed">{label}</Text>
    </Stack>
  );
}

export default function OverviewTab({
  facilitator,
  courseCount,
  enrolmentCount,
  locationCount,
  onRemoved,
  onTabChange,
}: OverviewTabProps) {
  const Facilitators = useFacilitatorService();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  if (!facilitator) {
    return (
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
        <Skeleton height={220} radius="md" />
        <Skeleton height={220} radius="md" />
      </SimpleGrid>
    );
  }

  const displayEmail = facilitator.email || 'N/A';
  const displayPhone = facilitator.phone || 'N/A';

  const confirmRemoveFacilitator = async () => {
    setRemoving(true);
    try {
      await Facilitators.delete(facilitator.userId);
      toast.warning('Facilitator Removed', 'Facilitator has been removed.');
      onRemoved?.();
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to remove facilitator.');
    } finally {
      setRemoving(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Stack gap="lg">
      <ConfirmActionModal
        opened={confirmOpen}
        title="Remove Facilitator"
        message="Are you sure you want to remove this facilitator? This action cannot be undone."
        onConfirm={confirmRemoveFacilitator}
        onClose={() => setConfirmOpen(false)}
      />

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
        {/* ── Facilitator Details ── */}
        <Card withBorder radius="md" padding="lg">
          <Text fw={700} mb="sm">Facilitator Details</Text>
          <Divider mb="md" />
          <Stack gap="md">
            <DetailRow icon={<IconMail size={18} />} label="Email" value={displayEmail} />
            <DetailRow icon={<IconPhone size={18} />} label="Phone" value={displayPhone} />
            <DetailRow
              icon={<IconBriefcase2 size={18} />}
              label="Active Enrolment"
              value={enrolmentCount}
            />
          </Stack>
        </Card>

        {/* ── Status & Assignment ── */}
        <Card withBorder radius="md" padding="lg">
          <Text fw={700} mb="sm">Status &amp; Assignment</Text>
          <Divider mb="md" />
          <Stack gap="md">
            <DetailRow
              icon={<IconCircleDot size={18} />}
              label="Status"
              value={
                <Badge
                  variant="light"
                  style={{
                    backgroundColor: facilitator.statusColor
                      ? `${facilitator.statusColor}22`
                      : undefined,
                    color: facilitator.statusColor ?? undefined,
                  }}
                >
                  {facilitator.statusName ?? 'Unknown Status'}
                </Badge>
              }
            />
            <DetailRow
              icon={<IconBook size={18} />}
              label="Courses"
              value={courseCount ? `${courseCount} assigned` : 'None assigned'}
            />
            <DetailRow
              icon={<IconMapPin size={18} />}
              label="Locations"
              value={locationCount ? `${locationCount} assigned` : 'None assigned'}
            />
          </Stack>
        </Card>
      </SimpleGrid>

      {/* ── Bottom stat bar (Courses / Enrolments / Locations) ── */}
      <Card withBorder radius="md" padding="lg">
        <Group justify="space-around">
          <StatItem
            icon={<IconBook size={20} />}
            count={courseCount}
            label="Courses"
            color="blue"
            onClick={() => onTabChange?.('courses')}
          />
          <StatItem
            icon={<IconBriefcase2 size={20} />}
            count={enrolmentCount}
            label="Enrolments"
            color="grape"
            onClick={() => onTabChange?.('registrations')}
          />
          <StatItem
            icon={<IconMapPin size={20} />}
            count={locationCount}
            label="Locations"
            color="pink"
            onClick={() => onTabChange?.('locations')}
          />
        </Group>
      </Card>

      <Group justify="flex-end">
        <Button
          color="red"
          variant="light"
          leftSection={<IconUserMinus size={16} />}
          loading={removing}
          onClick={() => setConfirmOpen(true)}
        >
          Remove Facilitator
        </Button>
      </Group>
    </Stack>
  );
}
